/**
 * Pure decision rule for alarm-driven sensitivity (Block 4).
 *
 * Extracted from the watcher in useRadarLogic.ts so the three-level priority rule (wartime >
 * powersave > normal) is unit-testable without Vue / stores / the pipeline. The watcher simply
 * calls this with the current `powerMode` + `alertActive` and applies the returned Stage-1
 * threshold and Stage-0 energy preset.
 *
 * Rationale (see ml/plans/stage0_preroll_hold_alarm.md, ml/reports/alarm_driven_sensitivity/):
 * during an active air-raid alert, recall is prioritised over battery — both gates relax so
 * quieter/marginal events still reach the heavy classifier. This is a NEW external trigger
 * (alarm state was never wired as a sensitivity lever before), so it does not alter any prior
 * experiment run — none of them ever had an active alert.
 */

import {
  RADAR_S1_THRESHOLD,
  RADAR_S1_THRESHOLD_POWERSAVE,
  RADAR_S1_THRESHOLD_WARTIME,
  RADAR_ENERGY_PRESET,
} from './useLiveDetection';
import type { CascadeConfig } from '@/types';

/** Power mode setting from the settings store. */
export type PowerMode = 'adaptive' | 'always' | 'alertOnly' | string;

/**
 * Result of the sensitivity decision: the Stage-1 threshold to apply and the Stage-0 energy
 * preset to switch to.
 */
export interface SensitivityDecision {
  stage1Threshold: number;
  energyPreset: NonNullable<CascadeConfig['energyFilterPreset']>;
  /** Which level was chosen — for diagnostics / logging. */
  level: 'wartime' | 'powersave' | 'normal';
}

/**
 * Pick the radar sensitivity level from the current power mode and air-raid alert state.
 *
 * Priority:
 *   1. WARTIME  — alertActive === true  → wartime threshold (0.15) + `sensitive` preset,
 *                 regardless of power mode (an active threat always wins).
 *   2. POWERSAVE — powerMode === 'adaptive' AND alertActive === false → powersave threshold
 *                 (0.75) + the radar default `sensitive` preset. The battery saving comes from the
 *                 stricter Stage-1 gate (−52 % Stage-2 wake-ups on the field night); the Stage-0
 *                 `balanced` preset this level used until 2026-09-19 saved only ~8 % more while
 *                 dropping fly-bys (GT corpus moving V2 4→1, field night −13 hits net), see
 *                 ml/reports/m1_cascade_operating_point/REPORT.md.
 *   3. NORMAL   — anything else (unknown alert / non-adaptive) → normal threshold (0.70) +
 *                 the radar default `sensitive` preset (see RADAR_ENERGY_PRESET in
 *                 useLiveDetection; the GT sweep promoted `sensitive` from wartime-only to
 *                 the unconditional default — its +2 V2 moving matches had no spurious
 *                 cost, so gating it behind an alert left recall on the table).
 *
 * Unknown alert status (null) intentionally falls through to NORMAL: missing an explosion is
 * worse than spending battery, so we do NOT relax the gates on missing data.
 */
export function pickSensitivity(
  powerMode: PowerMode,
  alertActive: boolean | null,
): SensitivityDecision {
  if (alertActive === true) {
    return {
      stage1Threshold: RADAR_S1_THRESHOLD_WARTIME,
      energyPreset: RADAR_ENERGY_PRESET,
      level: 'wartime',
    };
  }
  if (powerMode === 'adaptive' && alertActive === false) {
    return {
      stage1Threshold: RADAR_S1_THRESHOLD_POWERSAVE,
      energyPreset: RADAR_ENERGY_PRESET,
      level: 'powersave',
    };
  }
  return {
    stage1Threshold: RADAR_S1_THRESHOLD,
    energyPreset: RADAR_ENERGY_PRESET,
    level: 'normal',
  };
}
